import React, { RefObject } from 'react';
import { SanityImage } from 'sanity-image';
import { Icon } from '@iconify/react';

import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';

interface SanityMediaAsset {
  _id: string;
}

interface BackgroundImage {
  asset?: SanityMediaAsset;
  hotspot?: {
    x: number;
    y: number;
  };
  crop?: {
    bottom: number;
    left: number;
    right: number;
    top: number;
  };
  alt?: string;
}

interface BuyBoxProps {
  buyBoxRef: RefObject<HTMLDivElement>;
  desktopBackground: BackgroundImage;
  mobileBackground: BackgroundImage;
  sanityProduct: any;
  getInternalLink: (link: any) => string;
}

const sanityBaseUrl = `https://cdn.sanity.io/images/${process.env.GATSBY_SANITY_PROJECT_ID}/${process.env.GATSBY_SANITY_DATASET}/`;

const BuyBox: React.FC<BuyBoxProps> = ({
  buyBoxRef,
  desktopBackground,
  mobileBackground,
  sanityProduct,
  getInternalLink,
}) => (
  <div className="relative" ref={buyBoxRef}>
    {desktopBackground?.asset?._id && (
      <SanityImage
        id={desktopBackground.asset._id}
        baseUrl={sanityBaseUrl}
        hotspot={desktopBackground.hotspot}
        crop={desktopBackground.crop}
        className="hidden lg:block w-full h-[calc(45vh)] lg:h-[calc(100vh-100px)] object-cover"
        width={1200}
        loading="eager"
        alt={desktopBackground.alt}
      />
    )}
    {mobileBackground?.asset?._id && (
      <SanityImage
        id={mobileBackground.asset._id}
        baseUrl={sanityBaseUrl}
        hotspot={mobileBackground.hotspot}
        crop={mobileBackground.crop}
        className="block lg:hidden w-full h-[calc(45vh)] lg:h-[calc(100vh-100px)] object-cover"
        width={500}
        loading="eager"
        alt={mobileBackground.alt}
      />
    )}
    <div className="w-full max-w-screen-xl mx-auto lg:absolute top-0 left-0 right-0">
      <div className="relative lg:z-0">
        <div className="lg:absolute bg-white right-4 top-14 lg:w-1/3 lg:border lg:rounded-lg lg:border-gray-300 p-4 lg:shadow-lg">
          <h1 className="mb-2 text-xl lg:text-2xl">
            {sanityProduct?.store?.title}
          </h1>
          {sanityProduct?.additionalProductInformation?.length > 0 && (
            <div className="block">
              <div className="gap-2 flex flex-col">
                {sanityProduct.additionalProductInformation.map(
                  additionalInfo => (
                    <div
                      key={additionalInfo._key}
                      className="gap-2 flex items-center"
                    >
                      {additionalInfo?.mediaType === 'picker' &&
                        additionalInfo?.icon?.icon && (
                          <Icon
                            icon={additionalInfo.icon.icon}
                            flip={additionalInfo.icon.metadata.flip}
                            rotate={additionalInfo.icon.metadata.rotate}
                            width={additionalInfo.icon?.metadata?.size?.width}
                            height={additionalInfo.icon?.metadata?.size?.height}
                            style={{
                              color: additionalInfo.icon?.metadata?.color?.hex,
                              display: 'inline-block',
                              width:
                                additionalInfo.icon?.metadata?.size?.width ||
                                16,
                              height:
                                additionalInfo.icon?.metadata?.size?.height ||
                                16,
                            }}
                            className={
                              additionalInfo.icon?.metadata?.color?.hex
                                ? ''
                                : 'text-foreground-headline'
                            }
                          />
                        )}
                      {additionalInfo?.mediaType === 'upload' &&
                        additionalInfo?.media?.asset?.url && (
                          <img
                            src={additionalInfo.media.asset.url}
                            width={16}
                            height={16}
                            className="max-w-fit"
                            alt={
                              additionalInfo?.media?.alt ||
                              additionalInfo?.headline ||
                              'Icon Image'
                            }
                          />
                        )}

                      <div className="w-full text-base font-normal [&>p]:mb-0 text-foreground [&>p]:leading-4">
                        <h2 className="text-lg font-bold mb-0">
                          {additionalInfo.headline}
                        </h2>
                        <BlockBuilder
                          blocks={additionalInfo.copy}
                          getInternalLink={getInternalLink}
                        />
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default BuyBox;
